import React, { useMemo } from 'react';
import { CalendarRange } from 'lucide-react';
import { localISO } from '../data/programs';

/**
 * RollingWindowGoal — replaces the binary streak counter.
 *
 * Why: dev package (UCL 2025 + estrogen-dopamine research) flags that streaks
 * shame users when broken — disproportionate disengagement, especially for
 * perimenopausal women and chronic-pain users. Rolling-window framing keeps
 * the encouragement without the cliff.
 *
 * Counts active days in the last 7 (and last 28 as deeper context). Frame:
 * "4 of the last 7 days — that's the pattern" beats "you broke your 5-day
 * streak yesterday."
 */
const RollingWindowGoal = ({ history, goalDaysPerWeek = 4 }) => {
  const { last7, last28, todayLogged } = useMemo(() => {
    const dates = new Set();
    (history || []).forEach((h) => {
      dates.add(localISO(new Date(h.date)));
    });

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayKey = localISO(today);

    let l7 = 0;
    let l28 = 0;
    for (let i = 0; i < 28; i++) {
      const d = new Date(today);
      d.setDate(d.getDate() - i);
      if (dates.has(localISO(d))) {
        l28++;
        if (i < 7) l7++;
      }
    }
    return { last7: l7, last28: l28, todayLogged: dates.has(todayKey) };
  }, [history]);

  // Visible 7 dots, today on the right (most recent)
  const days = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = new Set((history || []).map((h) => localISO(new Date(h.date))));
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(today);
      d.setDate(d.getDate() - (6 - i));
      return { iso: localISO(d), active: dates.has(localISO(d)) };
    });
  }, [history]);

  const goalMet = last7 >= goalDaysPerWeek;
  const subText = (() => {
    if (last7 === 0) return 'Whenever you train next is the start.';
    if (goalMet) return `You're tracking your ${goalDaysPerWeek}/week pattern.`;
    if (last7 >= goalDaysPerWeek - 1) return 'One more day this week lands the pattern.';
    return `Pattern target: ${goalDaysPerWeek}/week. You've got room.`;
  })();

  return (
    <div
      className="flex items-center gap-3 px-4 py-2.5 bg-enf-surface-deep border border-enf-surface-border rounded-md"
      data-testid="rolling-window-goal"
    >
      <CalendarRange className="w-5 h-5 text-enf-role-info" />
      <div>
        <div className="font-display text-2xl tracking-wide leading-none">
          <span data-testid="rwg-last7">{last7}</span>
          <span className="text-enf-text-muted text-base mx-1">/ 7</span>
          <span className="text-enf-text-muted text-base">last week</span>
        </div>
        <div className="flex items-center gap-2 mt-1.5">
          <div className="flex gap-0.5" aria-label="Last 7 days activity">
            {days.map((d, idx) => (
              <span
                key={d.iso}
                className={`w-1.5 h-1.5 rounded-full ${
                  d.active
                    ? (idx === 6 ? 'bg-enf-accent' : 'bg-enf-role-info')
                    : 'bg-enf-surface-elevated'
                }`}
                title={d.iso + (d.active ? ' · trained' : '')}
              />
            ))}
          </div>
          <span className="text-enf-text-muted text-xs">· {last28}/28 monthly</span>
        </div>
        <div className="text-enf-text-muted text-xs mt-1">{subText}</div>
      </div>
      {todayLogged && (
        <span className="ml-1 px-2 py-0.5 rounded-full bg-enf-accent/15 border border-enf-accent-fg/30 text-enf-accent-fg text-[10px] uppercase tracking-widest font-semibold">
          Today
        </span>
      )}
    </div>
  );
};

export default RollingWindowGoal;
